import React from 'react';

import { Image } from 'components/ui';
import { useModalDispatch } from 'context/ModalContext';
import { MODAL_TYPES } from 'helpers/LayoutHelpers';
import { ORDER_NOTICE_TYPES } from 'helpers/OrderHelpers';
import { cx } from 'helpers/StylingHelpers';
import { useUser } from 'hooks/data/useUser';
import { useAnalytics } from 'hooks/useAnalytics';

import Alert, { VARIANTS } from '../Alert/Alert';

import type {
  DataTestId, INotice, NoticeActionModalName, Size
} from 'types';
import { NOTICE_ACTIONS_TYPES, NOTICE_ACTION_MODAL_NAMES, Sizes } from 'types';

import styles from './Notice.module.scss';

const VARIANTS_MAP = {
  [ORDER_NOTICE_TYPES.DEFAULT]: VARIANTS.WARNING,
  [ORDER_NOTICE_TYPES.INFO]: VARIANTS.INFO,
  [ORDER_NOTICE_TYPES.WARNING]: VARIANTS.WARNING,
  [ORDER_NOTICE_TYPES.SUCCESS]: VARIANTS.SUCCESS,
  [ORDER_NOTICE_TYPES.ERROR]: VARIANTS.ERROR,
};

// Types

interface Props extends DataTestId {
  classNames?: {
    root?: string,
    icon?: string
  },
  //
  key?: string | number,
  size?: Size,
  iconSize?: number,
  //
  notice: INotice,
  disabled?: boolean,
  withMarkdown?: boolean,
  withCircledIcon?: boolean,
  //
  onClick?: (e: React.MouseEvent) => void,
  //
  children?: React.ReactNode
}

const Notice = (props: Props) => {

  const {
    //
    classNames = {},
    //
    size = Sizes.MEDIUM,
    iconSize,
    //
    notice,
    withMarkdown,
    withCircledIcon = true,
    //
    dataTestId,
    //
    onClick,
    //
    children,
    //
    ...rest
    //
  } = props;

  // Hooks

  const analytics = useAnalytics();
  const { addModalToStack } = useModalDispatch();

  // State Hooks

  const [isDisabled, setIsDisabled] = React.useState(false);

  // Props

  const {
    details,
    action
  } = notice || {};

  const {
    type,
    icon,
    title,
    description,
    theme
  } = details || {};

  const {
    backgroundColor,
    textColor,
    titleTextColor,
    bodyTextColor,
    iconBackgroundColor
  } = theme || {};

  const href = action?.type === NOTICE_ACTIONS_TYPES.LINK && 'url' in action ? action?.url : undefined;
  const modalName = action?.type === NOTICE_ACTIONS_TYPES.MODAL && 'name' in action ? action?.name : undefined;

  const variant = type
    ? VARIANTS_MAP[type] ?? 'default' : 'default';

  const hasImageIcon = typeof icon === 'string';

  // Data Hooks

  const {
    data: user,
    isLoading: isLoadingUser
  } = useUser({ isOptional: true }, {
    enabled: modalName && [NOTICE_ACTION_MODAL_NAMES.PHONE_VALIDATION].includes(modalName),
  });

  // Props

  const { phoneNumber } = user || {};

  // Icon

  const AlertIcon = !icon ? null : (
    !hasImageIcon ? icon : (
      <div
        className={cx(
          styles.icon,
          { [styles[size]]: size },
        )}
        style={{
          width: iconSize,
          height: iconSize,
        }}
      >
        <Image
          src={icon}
          fill
          style={{
            objectFit: 'contain'
          }}
          alt="Notice Icon"
        />
      </div>
    )
  );

  // Handlers

  const handleModalOpen = (name: NoticeActionModalName) => {
    switch (name) {

      // Phone Verification
      case NOTICE_ACTION_MODAL_NAMES.PHONE_VALIDATION: {
        analytics.selectPhoneNumberVerificationNotice();
        addModalToStack({
          id: MODAL_TYPES.PHONE_VERIFICATION,
          props: {
            initialPhoneNumber: phoneNumber,
            onSuccess: () => {
              setIsDisabled(true);
            }
          }
        });
        break;
      }

      // Default
      default:
        break;

    }
  };

  // Render

  return (
    <Alert
      classNames={{
        root: cx(
          styles.root,
          { [styles.disabled]: isDisabled || isLoadingUser },
          classNames?.root,
        ),
        icon: classNames?.icon,
      }}
      //
      variant={theme ? VARIANTS.CUSTOM : variant}
      icon={AlertIcon}
      title={title}
      description={description}
      size={size}
      withCircledIcon={withCircledIcon}
      withMarkdown={withMarkdown}
      //
      theme={{
        backgroundColor,
        titleColor: titleTextColor || textColor,
        textColor: bodyTextColor || textColor,
        iconColor: hasImageIcon ? '' : textColor,
        iconBackgroundColor
      }}
      //
      onClick={(!modalName && !onClick) ? undefined : (e) => {
        onClick?.(e);
        if (modalName) {
          handleModalOpen(modalName);
        }
      }}
      //
      dataTestId={dataTestId}
      //
      {...rest}
      //
      href={href}
    >
      {children}
    </Alert>
  );
};

export default Notice;
